import { memo } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { swapMeshUV, applyPBR, unifyRepeatsFromBase, loadTextureMaps } from './textureUtils'

function OttomanLegsCmp({
  ottomanUrl,
  legUrl,
  texture,
  legTypeId,
  edgeInset,
}: {
  ottomanUrl: string
  legUrl: string
  texture: any
  legTypeId?: number
  edgeInset?: number
}) {
  // Get ottoman bounding box for positioning legs
  // The ottoman bounding box is stored in world space (already positioned)
  const ottomanBox = (window as any).__ottoman_bbox__
  if (!ottomanBox || ottomanBox.isEmpty()) return null as any

  // For ottoman legs, legUrl might be null - handle this case
  if (!legUrl) {
    return null
  }
  
  const leg = useLoader(FBXLoader, legUrl)
  const legBox = new THREE.Box3().setFromObject(leg)
  const legCenter = legBox.getCenter(new THREE.Vector3())
  const legMin = legBox.min
  const BASE_SCALE = 0.05
  const legY = -legBox.min.y * BASE_SCALE
  // Derive from bed leg edge_inset (half); fall back to prior ~0.25 default
  const bedInset = Number.isFinite(edgeInset) ? Number(edgeInset) : 0.48
  const inset = bedInset / 2
  
  // Compute leg dimensions in scaled world space for positioning adjustments
  const legWidth = (legBox.max.x - legBox.min.x) * BASE_SCALE
  const legDepth = (legBox.max.z - legBox.min.z) * BASE_SCALE
  const legHalfWidth = legWidth / 2
  const legHalfDepth = legDepth / 2
  
  // Offset multiplier - reduces the offset amount (0.5 = half offset, 1.0 = full offset)
  // Adjust this value to fine-tune leg positioning
  const offsetMultiplier = 0.6 // Use 60% of half-width/depth for offset
  const adjustedHalfWidth = legHalfWidth * offsetMultiplier
  const adjustedHalfDepth = legHalfDepth * offsetMultiplier
  
  // Small safety margin to prevent legs from crossing ottoman footprint
  const safetyMargin = 0.005 // 0.5cm in world units (reduced)
  
  // Debug flag (set to true for development debugging)
  const DEBUG_OTTOMAN_LEGS = false
  
  if (DEBUG_OTTOMAN_LEGS) {
    const ottomanWidth = ottomanBox.max.x - ottomanBox.min.x
    const ottomanDepth = ottomanBox.max.z - ottomanBox.min.z
    console.log('[OttomanLegs] Bounding boxes:', {
      ottoman: { width: ottomanWidth, depth: ottomanDepth },
      leg: { width: legWidth, depth: legDepth },
      legHalfExtents: { width: legHalfWidth, depth: legHalfDepth }
    })
  }

  // Determine leg positions based on leg type (same logic as bed legs)
  // Positions are relative to ottoman's world position
  const getLegPositions = () => {
    const ottomanWidth = ottomanBox.max.x - ottomanBox.min.x
    const ottomanDepth = ottomanBox.max.z - ottomanBox.min.z
    
    // Base corner positions relative to ottoman (in world space)
    const corners: [number, number][] = [
      [ottomanBox.min.x, ottomanBox.max.z], // Front left
      [ottomanBox.max.x, ottomanBox.max.z], // Front right
      [ottomanBox.min.x, ottomanBox.min.z], // Back left
      [ottomanBox.max.x, ottomanBox.min.z], // Back right
    ]
    
    // Additional positions for 8-leg configuration
    const additionalPositions: [number, number][] = [
      [ottomanBox.min.x + ottomanWidth/2, ottomanBox.max.z], // Front left inner
      [ottomanBox.max.x - ottomanWidth/2, ottomanBox.max.z], // Front right inner
      [ottomanBox.min.x + ottomanWidth/2, ottomanBox.min.z], // Back left inner
      [ottomanBox.max.x - ottomanWidth/2, ottomanBox.min.z], // Back right inner
    ]
    
    const rotations = [
      [0, -Math.PI/2, 0], // Front left
      [0, 0, 0],          // Front right
      [0, Math.PI, 0],    // Back left
      [0, Math.PI/2, 0],  // Back right
    ]
    
    const additionalRotations = [
      [0, -Math.PI/2, 0], // Front left inner
      [0, 0, 0],          // Front right inner
      [0, Math.PI, 0],    // Back left inner
      [0, Math.PI/2, 0],  // Back right inner
    ]

    // Determine leg count based on legTypeId
    // legTypeId: 1 = 4 legs, 2 = 8 legs, 3 = bed base
    if (legTypeId === 2) {
      return {
        positions: [
          ...corners.map(([x, z], i) => {
            let adjX = x
            let adjZ = z
            if (i === 0 || i === 2) adjX += inset
            if (i === 1 || i === 3) adjX -= inset
            if (i === 0 || i === 1) adjZ -= inset
            if (i === 2 || i === 3) adjZ += inset
            return [adjX, adjZ]
          }),
          ...additionalPositions.map(([x, z], i) => {
            let adjX = x
            let adjZ = z
            if (i === 0 || i === 2) adjX += inset
            if (i === 1 || i === 3) adjX -= inset
            if (i === 0 || i === 1) adjZ -= inset
            if (i === 2 || i === 3) adjZ += inset
            return [adjX, adjZ]
          })
        ],
        rotations: [...rotations, ...additionalRotations]
      }
    } else if (legTypeId === 3) {
      // Bed base - single centered position
      return {
        positions: [[ottomanBox.min.x + ottomanWidth/2, ottomanBox.min.z + ottomanDepth/2]],
        rotations: [[0, 0, 0]]
      }
    } else {
      // Default: 4 legs with inset
      return {
        positions: corners.map(([x, z], i) => {
          let adjX = x
          let adjZ = z
          if (i === 0 || i === 2) adjX += inset
          if (i === 1 || i === 3) adjX -= inset
          if (i === 0 || i === 1) adjZ -= inset
          if (i === 2 || i === 3) adjZ += inset
          return [adjX, adjZ]
        }),
        rotations
      }
    }
  }

  const { positions, rotations } = getLegPositions()

  return (
    <>
      {positions.map((position, i) => {
        const clone = leg.clone()
        clone.scale.setScalar(BASE_SCALE)
        
        // For bed bases, center the model under the ottoman
        if (legTypeId === 3) {
          const ottomanCenterX = (ottomanBox.min.x + ottomanBox.max.x) / 2
          const ottomanCenterZ = (ottomanBox.min.z + ottomanBox.max.z) / 2
          clone.position.set(
            ottomanCenterX - legCenter.x * BASE_SCALE, 
            -legMin.y * BASE_SCALE, 
            ottomanCenterZ - legCenter.z * BASE_SCALE
          )
        } else {
          // For regular legs, use the original positioning
          clone.position.set(-legCenter.x * BASE_SCALE, -legMin.y * BASE_SCALE, -legCenter.z * BASE_SCALE)
        }
        
        clone.updateMatrixWorld(true)

        if (texture?.base_color_url || texture?.base_color_urls) {
          clone.traverse((child: any) => {
            if (child.isMesh && child.material) {
              try {
                child.geometry = (child.geometry as THREE.BufferGeometry).clone()
                if (child.geometry && child.geometry.userData) {
                  delete child.geometry.userData._origUV
                }
              } catch {}
              swapMeshUV(child, true, `ottoman-leg-${i}`)
              const mat = new THREE.MeshStandardMaterial()
              mat.color = new THREE.Color(0xffffff)
              ;(mat as any).emissive?.setRGB?.(0, 0, 0)
              ;(mat as any).vertexColors = false
              
              const loader = new THREE.TextureLoader()
              const maps = loadTextureMaps(texture, loader)
              
              if (maps.base) {
                maps.base.flipY = false
                // @ts-ignore
                if ((THREE as any).SRGBColorSpace) {
                  // @ts-ignore
                  maps.base.colorSpace = (THREE as any).SRGBColorSpace
                }
                maps.base.wrapS = maps.base.wrapT = THREE.RepeatWrapping
                if ((maps.base as any).center && typeof (maps.base as any).center.set === 'function') {
                  ;(maps.base as any).center.set(0.5, 0.5)
                }
              }
              
              applyPBR({
                mat,
                origMat: child.material,
                type: (texture as any)?.material_type || (texture as any)?.materialType || 'wood',
                meshName: child.name,
                maps,
                rotateTurns: 1,
                hasUV2: !!child.geometry.attributes.uv2,
                repeat: { x: 1, y: 1 },
                offset: { x: 0, y: 0 },
              })
              unifyRepeatsFromBase(mat as any)
              child.material = mat
            }
          })
        }

        clone.traverse((child: any) => {
          if (child.isMesh) {
            child.castShadow = true
            child.receiveShadow = true
          }
        })

        // For bed bases, position at ottoman center; for legs, use individual positions
        let groupPosition: [number, number, number]
        if (legTypeId === 3) {
          // Bed base: centered under ottoman
          groupPosition = [0, legY as number, 0]
        } else {
          // Regular legs: adjust position to align outer corner with ottoman edge
          // The position array contains the desired outer corner location (after inset)
          // We need to offset inward by half the leg width/depth so the leg's outer corner aligns
          let offsetX = 0
          let offsetZ = 0
          
          const isEightLegConfig = legTypeId === 2
          const isCornerLeg = i < 4
          const isInnerLeg = isEightLegConfig && i >= 4
          
          if (isCornerLeg) {
            // Corner legs: offset based on which corner
            // Front left (0): offset right (+X) and back (+Z)
            // Front right (1): offset left (-X) and back (+Z)
            // Back left (2): offset right (+X) and front (-Z)
            // Back right (3): offset left (-X) and front (-Z)
            // NOTE: Width offset works, but depth offset needs reversed signs
            if (i === 0) {
              // Front left: move right and back
              offsetX = adjustedHalfWidth - safetyMargin
              offsetZ = -(adjustedHalfDepth - safetyMargin) // Reversed sign
            } else if (i === 1) {
              // Front right: move left and back
              offsetX = -(adjustedHalfWidth - safetyMargin)
              offsetZ = -(adjustedHalfDepth - safetyMargin) // Reversed sign
            } else if (i === 2) {
              // Back left: move right and front
              offsetX = adjustedHalfWidth - safetyMargin
              offsetZ = adjustedHalfDepth - safetyMargin // Reversed sign
            } else if (i === 3) {
              // Back right: move left and front
              offsetX = -(adjustedHalfWidth - safetyMargin)
              offsetZ = adjustedHalfDepth - safetyMargin // Reversed sign
            }
          } else if (isInnerLeg) {
            // Inner legs (8-leg config): positioned at midpoints of edges
            // Front left inner (4): on front edge, left side - offset right (+X), back (+Z)
            // Front right inner (5): on front edge, right side - offset left (-X), back (+Z)
            // Back left inner (6): on back edge, left side - offset right (+X), front (-Z)
            // Back right inner (7): on back edge, right side - offset left (-X), front (-Z)
            const innerIndex = i - 4
            if (innerIndex === 0) {
              // Front left inner: move right and back
              offsetX = adjustedHalfWidth - safetyMargin
              offsetZ = -(adjustedHalfDepth - safetyMargin) // Reversed sign
            } else if (innerIndex === 1) {
              // Front right inner: move left and back
              offsetX = -(adjustedHalfWidth - safetyMargin)
              offsetZ = -(adjustedHalfDepth - safetyMargin) // Reversed sign
            } else if (innerIndex === 2) {
              // Back left inner: move right and front
              offsetX = adjustedHalfWidth - safetyMargin
              offsetZ = adjustedHalfDepth - safetyMargin // Reversed sign
            } else if (innerIndex === 3) {
              // Back right inner: move left and front
              offsetX = -(adjustedHalfWidth - safetyMargin)
              offsetZ = adjustedHalfDepth - safetyMargin // Reversed sign
            }
          }
          
          groupPosition = [
            (position[0] as number) + offsetX,
            legY as number,
            (position[1] as number) + offsetZ
          ]
        }
        
        return (
          <group key={i} position={groupPosition as [number, number, number]} rotation={rotations[i] as [number, number, number]}>
            <primitive object={clone} />
          </group>
        )
      })}
    </>
  )
}

const OttomanLegs = memo(OttomanLegsCmp)
export default OttomanLegs

